import {useCallback, useEffect, useRef, useState, type RefObject} from 'react';
import {personalOwner, requirePersonalOwner} from '@/features/appearance/session';

/** View callbacks belong to the mounted account, including anonymous previews.
 * This is a view lease; callers still own transport authorization/receipts. */
export function useOverlayCurrent() {
  const [owner] = useState(personalOwner);
  const mounted = useRef(true);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);
  return useCallback(() => {
    if (!mounted.current) return false;
    try {requirePersonalOwner(owner); return true;} catch {return false;}
  }, [owner]);
}

/** Keep focus in the visible modal and restore the actual opener on close.
 * Language and callback changes do not rerun the initial focus operation. */
export function useOverlayFocus(ref: RefObject<HTMLDivElement | null>, close: () => void) {
  const latestClose = useRef(close);
  useEffect(() => {latestClose.current = close;}, [close]);
  useEffect(() => {
    const dialog = ref.current, previous = document.activeElement;
    if (!dialog) return;
    const nodes = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not(:disabled),input:not(:disabled),textarea:not(:disabled),select:not(:disabled),a[href],[tabindex="0"]',
    )).filter(node => node.tabIndex >= 0 && !node.closest('[hidden]') &&
      (typeof node.checkVisibility !== 'function' || node.checkVisibility()));
    (nodes()[0] ?? dialog).focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {event.preventDefault(); event.stopPropagation(); latestClose.current();}
      if (event.key !== 'Tab') return;
      const items = nodes(), first = items[0], last = items.at(-1);
      if (!first) {event.preventDefault(); dialog.focus();}
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {event.preventDefault(); last?.focus();}
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) {event.preventDefault(); first.focus();}
    };
    dialog.addEventListener('keydown', keydown);
    return () => {dialog.removeEventListener('keydown', keydown); if (previous instanceof HTMLElement && previous.isConnected) previous.focus();};
  }, [ref]);
}
